import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { useBasket } from "@/lib/basket";
import { BrandMark } from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, CheckCircle2, FileDown, ArrowLeft, MailCheck } from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/helpers";

export default function BasketSuccess() {
  const id = new URLSearchParams(window.location.search).get("basket");
  const { clear } = useBasket();
  const [data, setData] = useState(null);

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data: d } = await api.get(`/basket/${id}/status`);
        setData(d);
        if (d.status === "paid") { clear(); return; }
      } catch { setData({ status: "error" }); return; }
      if (++tries < 15) timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const paid = data?.status === "paid";
  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border">
        <div className="max-w-3xl mx-auto h-full flex items-center justify-between px-4 sm:px-6">
          <BrandMark />
          <Link to="/shop" data-testid="basket-success-shop"><Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-1.5" />Return to course shop</Button></Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        {!data || (!paid && data.status !== "error" && data.status !== "pending") ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        ) : !paid ? (
          <Card className="p-8 text-center" data-testid="basket-success-pending">
            <Loader2 className="h-7 w-7 animate-spin text-primary mx-auto" />
            <p className="mt-3 text-muted-foreground">{data.status === "error" ? "We couldn't find this booking." : "Confirming your payment…"}</p>
          </Card>
        ) : (
          <Card className="p-8" data-testid="basket-success-paid">
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
              <h1 className="font-heading text-2xl font-bold mt-3">Booking confirmed!</h1>
              <p className="text-sm text-muted-foreground mt-2 flex items-center justify-center gap-1.5"><MailCheck className="h-4 w-4" />Your receipt has been emailed to {data.buyer_email}. Each learner has been emailed their own access link.</p>
              <p className="text-sm text-muted-foreground mt-1">Please check junk or spam folders if emails aren't in the inbox.</p>
            </div>
            <div className="mt-6 space-y-3">
              {data.lines.map((ln, i) => (
                <div key={i} className="rounded-lg border border-border p-4" data-testid={`basket-success-line-${i}`}>
                  <p className="font-medium">{ln.course_title}</p>
                  {ln.session && <p className="text-xs text-muted-foreground">{fmtDate(ln.session.start)} · {fmtTime(ln.session.start)}</p>}
                  <ul className="text-sm mt-2 space-y-0.5">{ln.learners.map((l, j) => <li key={j}>{l.name} <span className="text-muted-foreground">· {l.email}</span></li>)}</ul>
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-6">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/basket/${data.invoice_token}/invoice`} data-testid="basket-success-invoice">
                <Button variant="outline"><FileDown className="h-4 w-4 mr-1.5" />Download invoice (£{data.total.toFixed(2)})</Button>
              </a>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
